import React, { useEffect, useState } from 'react';
import Moment from 'react-moment';
import { ContractService } from 'services';
import { MONTHLY_FUND_STATUS, CONTRACT_STATUS } from 'helpers/constants';
import { useIsAdmin, useStore } from 'hooks';
import Menu from 'components/menu';
import Table from 'components/table';
import ActionsTable from 'components/table/actions';
import { Actions, Filter } from 'components/applications';
import Modal from 'components/modal';
import { Button } from 'components/ui/form';
import Contract from 'components/modal/contract';
import styles from './contracts.module.scss';

const CONTRACTS_ARCHIVE_MODE_STORAGE_KEY = 'granit_filters_contracts_main';
const DEFAULT_LIMIT = 20;

const ContractsPage = ({
  match: {
    params: { userId },
  },
}) => {
  const { reload, setUser, setLoading, setReload } = useStore();
  const [edit, setEdit] = useState(null);
  const [modal, setModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [archiveMode, setArchiveMode] = useState(() => {
    try {
      const stored = localStorage.getItem(CONTRACTS_ARCHIVE_MODE_STORAGE_KEY);
      return stored === 'archive' || stored === 'active' ? stored : 'active';
    } catch {
      return 'active';
    }
  });
  const isAdmin = useIsAdmin();

  useEffect(() => {
    try {
      localStorage.setItem(CONTRACTS_ARCHIVE_MODE_STORAGE_KEY, archiveMode);
    } catch {
      // ignore localStorage errors
    }
  }, [archiveMode]);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      const service = new ContractService({ setUser });
      setLoading(true);
      const response = await service.getContracts({
        userId,
        isArchive: archiveMode === 'archive' ? 1 : 0,
        page,
        limit,
      });
      setLoading(false);

      if (cancelled || !response) return;
      const items = Array.isArray(response) ? response : response.items;
      setContracts(items || []);
      setCount(Array.isArray(response) ? response.length : response.count ?? 0);
    }

    fetchData();
    return () => { cancelled = true; };
  }, [reload, userId, setUser, setLoading, archiveMode, page, limit]);

  const onOpenModal = () => setModal(true);

  const onCloseModal = (isReload = false) => {
    setEdit(null);
    setModal(false);
    if (isReload) {
      setReload();
    }
  };

  const onImportContract = async (e) => {
    const { target: { form, files } } = e;

    try {
      if (files.length) {
        const file = files[0];
        const formData = new FormData();
        formData.append('file', file, 'file.xml');
        formData.append('userId', userId);

        setLoading(true);
        const response = await new ContractService({ setUser }).importContract(formData);

        if (response && Array.isArray(response.contracts)) {
          setReload();
        }
      }
    } finally {
      setLoading(false);
      form.reset();
    }
  };

  const onImportBalance = async (e) => {
    const { target: { form, files } } = e;

    if (files.length) {
      const file = files[0];
      const formData = new FormData();
      formData.append('file', file, 'file.xml');

      setLoading(true);
      await new ContractService({ setUser }).importBalance(formData);
      setLoading(false);

      setReload();
    }

    form.reset();
  };

  const onDelete = async () => {
    if (!deleteItem) return;
    setLoading(true);
    const result = await new ContractService({ setUser }).deleteContract(deleteItem.id);
    setLoading(false);
    setDeleteItem(null);
    if (result) {
      setReload();
    }
  };

  const isArchiveView = archiveMode === 'archive';

  const onArchiveModeChange = (mode) => {
    setArchiveMode(mode);
    setPage(0);
  };

  const cells = [{
    name: 'Название',
    renderField: 'name',
  }, {
    name: 'Дата договора',
    nowrap: true,
    onRender: ({ date }) => <Moment>{date}</Moment>,
  }, {
    name: 'Баланс',
    renderField: 'balance',
  },
  {
    name: 'Отсрочка платежа',
    nowrap: true,
    onRender: ({ isPaymentDeferralProvided, paymentDeferralEndDate }) => (
      Number(isPaymentDeferralProvided) === 1 && paymentDeferralEndDate
        ? <Moment format="DD.MM.YYYY">{paymentDeferralEndDate}</Moment>
        : 'Нет'
    ),
  },
  {
    name: 'Месячный фонд',
    nowrap: true,
    onRender: ({ monthlyFund }) => MONTHLY_FUND_STATUS.toString(Number(monthlyFund)),
  },
  {
    name: 'В архиве',
    nowrap: true,
    onRender: ({ isArchive }) => isArchive ? 'Да' : 'Нет',
  },{
    name: 'Статус',
    nowrap: true,
    onRender: ({ status }) => CONTRACT_STATUS.toString(status),
  }, {
    name: '',
    right: true,
    nowrap: true,
    onRender: item => (
      <ActionsTable
        data={[
          {
            name: 'edit',
            title: isAdmin ? 'Редактировать контракт' : 'Посмотреть контракт',
            onClick: () => {
              setEdit(item);
              setModal(true);
            },
          },
          ...(isAdmin ? [{
            name: 'cart',
            title: 'Удалить',
            onClick: () => setDeleteItem(item),
          }] : []),
        ]}
      />
    ),
  }];

  return (
    <>
      <div className={styles.menu}>
        <Menu
          textAdd="Добавить"
          titleAdd="Добавить контракт"
          onAdd={isAdmin ? onOpenModal : false}
        />
      </div>
      {isAdmin ? (
        <Actions
          importText={[
            'Импорт контракта',
            'Импорт баланса',
          ]}
          onImport={[
            onImportContract,
            onImportBalance,
          ]}
        />
      ) : null}
      <Filter
        hideMenu
        showFilter={false}
        query={{}}
        setQuery={() => {}}
        filters={{}}
        showArchiveModeToggle
        archiveMode={archiveMode}
        onArchiveModeChange={onArchiveModeChange}
      />
      <div className={styles.table}>
        <Table
          cells={cells}
          data={contracts}
          count={count}
          page={page}
          limit={limit}
          onPageChange={({ selected }) => setPage(selected)}
          onLimitChange={(newLimit) => {
            setLimit(newLimit);
            setPage(0);
          }}
        />
      </div>
      {modal ? (
        <Modal closeBackground={false} onClose={onCloseModal}>
          <Contract edit={edit} onClose={onCloseModal} />
        </Modal>
      ) : null}
      {deleteItem ? (
        <Modal variant="dialog" onClose={() => setDeleteItem(null)}>
          <div className={styles.confirm}>
            <div className={styles.confirmText}>
              Вы уверены, что хотите удалить договор
              {' '}
              <b>{deleteItem.name}</b>
              ?
            </div>
            <div className={styles.confirmActions}>
              <Button color="lightgray" onClick={() => setDeleteItem(null)}>
                Отмена
              </Button>
              <Button onClick={onDelete}>Удалить</Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
};

export default ContractsPage;
